import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type GalleryViewMode = 'grid' | 'list' | 'timeline';
export type ModalType =
  | 'photo-detail'
  | 'confirm-delete'
  | 'create-event'
  | 'upload'
  | 'face-assign'
  | 'export'
  | null;

interface UiState {
  sidebarCollapsed: boolean;
  activeModal: ModalType;
  modalData: Record<string, unknown> | null;
  galleryViewMode: GalleryViewMode;
  selectedPhotoIds: Set<string>;
  isSelectionMode: boolean;

  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  openModal: (modal: ModalType, data?: Record<string, unknown>) => void;
  closeModal: () => void;
  setGalleryViewMode: (mode: GalleryViewMode) => void;
  togglePhotoSelection: (photoId: string) => void;
  selectPhotos: (photoIds: string[]) => void;
  deselectAllPhotos: () => void;
  setSelectionMode: (enabled: boolean) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      activeModal: null,
      modalData: null,
      galleryViewMode: 'grid',
      selectedPhotoIds: new Set<string>(),
      isSelectionMode: false,

      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),

      openModal: (modal, data) =>
        set({ activeModal: modal, modalData: data ?? null }),

      closeModal: () =>
        set({ activeModal: null, modalData: null }),

      setGalleryViewMode: (mode) =>
        set({ galleryViewMode: mode }),

      togglePhotoSelection: (photoId) =>
        set((state) => {
          const ids = new Set(state.selectedPhotoIds);
          if (ids.has(photoId)) {
            ids.delete(photoId);
          } else {
            ids.add(photoId);
          }
          return { selectedPhotoIds: ids, isSelectionMode: ids.size > 0 };
        }),

      selectPhotos: (photoIds) =>
        set({
          selectedPhotoIds: new Set(photoIds),
          isSelectionMode: photoIds.length > 0,
        }),

      deselectAllPhotos: () =>
        set({ selectedPhotoIds: new Set<string>(), isSelectionMode: false }),

      setSelectionMode: (enabled) =>
        set((state) => ({
          isSelectionMode: enabled,
          selectedPhotoIds: enabled ? state.selectedPhotoIds : new Set<string>(),
        })),
    }),
    {
      name: 'photo-improver-ui',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        galleryViewMode: state.galleryViewMode,
      }),
    },
  ),
);
